<script setup lang="ts">
/**
 * Multi-line Field. Same label / control / hint shell; `rows` sets the resting height.
 * With `autogrow` the control also follows the text as it is typed, up to `maxRows`,
 * after which it scrolls — a long instruction stays readable without a drag handle
 * (founder 2026-09-21).
 */
const props = withDefaults(defineProps<{ label: string; modelValue?: string; placeholder?: string; hint?: string; rows?: number; mono?: boolean; autogrow?: boolean; maxRows?: number }>(), { modelValue: '', rows: 3, placeholder: undefined, hint: undefined, maxRows: undefined })
const emit = defineEmits<{ 'update:modelValue': [v: string] }>()
const control = ref<HTMLTextAreaElement | null>(null)

/** Resting height comes from `rows`; growth is measured from the text itself. */
function fit() {
  const node = control.value
  if (!node || !props.autogrow) return
  const style = getComputedStyle(node)
  const frame = Number.parseFloat(style.borderTopWidth) + Number.parseFloat(style.borderBottomWidth)
  const padding = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom)
  const ceiling = Number.parseFloat(style.lineHeight) * (props.maxRows ?? props.rows * 2) + padding + frame
  node.style.height = 'auto'
  const wanted = node.scrollHeight + frame
  node.style.height = `${Math.min(wanted, ceiling)}px`
  node.style.overflowY = wanted > ceiling ? 'auto' : 'hidden'
}

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value)
  fit()
}

onMounted(fit)
watch(() => props.modelValue, () => nextTick(fit))
</script>

<template>
  <label class="flex flex-col gap-1.5">
    <span class="text-xs text-muted-foreground">{{ label }}</span>
    <textarea
ref="control" :value="modelValue" :placeholder="placeholder" :rows="rows" :class="mono ? 'num' : ''"
      class="resize-none rounded-[var(--radius-control)] border border-border bg-muted/40 px-2.5 py-1.5 text-[length:var(--type-control)] leading-[1.5] text-foreground placeholder:text-muted-foreground/60 focus:border-primary/60 focus:outline-none"
      @input="onInput" />
    <span v-if="hint" class="num text-[length:var(--type-fine)] text-muted-foreground">{{ hint }}</span>
  </label>
</template>
